import { FUNCTIONS_BASE_URL } from "./firebase";

// Single source of truth lives in firebase.ts, derived from the resolved
// Firebase project id so dev/prod never diverge from Firestore.
const FUNCTIONS_BASE = FUNCTIONS_BASE_URL;

// ── Diagnostics ────────────────────────────────────────────────────────────
//
// Every "No availability" report on the patient booking widget has
// historically been indistinguishable from a genuine empty schedule vs. a
// swallowed network/App-Check/server error, because callers only saw a
// generic toast and the UI collapsed to the same "no slots" visual state
// either way. These structured, tagged console logs (component:
// "booking-widget") let a specific report be reconstructed from browser
// console logs alone: what request was sent, and whether the response was a
// real slot count or a real error (status/message).
function logWidget(event: string, ctx: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.info(
    `[booking-widget] ${event}`,
    JSON.stringify({ component: "booking-widget", event, ...ctx }),
  );
}

function logWidgetError(event: string, ctx: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.error(
    `[booking-widget] ${event}`,
    JSON.stringify({ component: "booking-widget", event, ...ctx }),
  );
}

// ── Booking-side hybrid encryption (no @workspace/crypto dep) ────────────
//
// Mirrors the wire-format produced by hybridEncryptForPublicKey in
// packages/crypto so that the mobile pharmacist app can decrypt with
// hybridDecryptWithPrivateKey / getBookingDecrypt().
//
// Format: "hybrid1:" + btoa(JSON.stringify({ wrappedKey, ciphertext }))
//   wrappedKey = bufToBase64(RSA-OAEP wrap of ephemeral AES key)
//   ciphertext = bufToBase64(iv || AES-GCM ct)   ← canonical framing
//
// Historically this file emitted `base64(iv) + "." + base64(ct)`, which the
// shared decrypt could not read. Bookings stored in that framing still exist
// and cannot be re-encrypted, so the shared decrypt accepts both; new
// bookings must use the canonical concatenated framing above.
//
// ⚠️ DRIFT WARNING — this wire format has THREE producers:
//   packages/crypto/src/crypto.ts        canonical, and the only consumer
//   artifacts/booking/src/lib/api.ts     this file (no workspace dependency)
//   artifacts/functions/src/lib/bookingPatientCrypto.ts
//                                        the WhatsApp path, which seals
//                                        server-side (Cloud Functions cannot
//                                        resolve a workspace TS package)
// They are ONE logical wire format and must change in lockstep. The agreement
// is asserted by artifacts/functions/test/crossSurfaceContracts.test.ts —
// drift here is otherwise silent until a pharmacist cannot open a booking.

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

async function importBookingPublicKey(spkiBase64: string): Promise<CryptoKey> {
  const bytes = Uint8Array.from(atob(spkiBase64), (c) => c.charCodeAt(0));
  return crypto.subtle.importKey(
    "spki",
    bytes.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["wrapKey"],
  );
}

async function hybridEncryptJson(
  publicKey: CryptoKey,
  patient: unknown,
): Promise<string> {
  const ephemeralKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ctBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    ephemeralKey,
    new TextEncoder().encode(JSON.stringify(patient)),
  );
  const ctBytes = new Uint8Array(ctBuf);
  const combined = new Uint8Array(iv.length + ctBytes.length);
  combined.set(iv, 0);
  combined.set(ctBytes, iv.length);
  const ciphertext = bufToBase64(combined);
  const wrappedKeyBuf = await crypto.subtle.wrapKey("raw", ephemeralKey, publicKey, {
    name: "RSA-OAEP",
  });
  const wrappedKey = bufToBase64(wrappedKeyBuf);
  return "hybrid1:" + btoa(JSON.stringify({ wrappedKey, ciphertext }));
}

async function getChainBookingPublicKey(pharmacyId: string): Promise<CryptoKey | null> {
  const { getDb } = await import("./firebase");
  const { doc, getDoc } = await import("firebase/firestore");
  const db = getDb();
  const pharmacySnap = await getDoc(doc(db, "pharmacies", pharmacyId));
  if (!pharmacySnap.exists()) return null;
  const chainId = pharmacySnap.data()?.chainId as string | undefined;
  if (!chainId) return null;
  const chainSnap = await getDoc(doc(db, "chains", chainId));
  if (!chainSnap.exists()) return null;
  const bookingPublicKeyB64 = chainSnap.data()?.bookingPublicKey as string | undefined;
  if (!bookingPublicKeyB64) return null;
  return importBookingPublicKey(bookingPublicKeyB64);
}

export interface PatientPreFill {
  name: string;
  dob: string;
  phone: string;
  email: string;
  symptomSummary: string;
}

export interface TimeSlot {
  date: string;
  startTime: string;
  endTime: string;
  available: boolean;
}

export interface BookingRoom {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export interface BookingPharmacy {
  id: string;
  name: string;
  address: string;
  eircode: string;
  primaryColor?: string;
  logoUrl?: string;
}

// Slot duration is a choice the patient makes at booking time — pharmacists
// no longer configure it. These are the only durations the backend accepts
// (see ALLOWED_SLOT_DURATIONS in functions/src/lib/slotValidator.ts).
export const SLOT_DURATION_OPTIONS: Array<{ value: 30 | 45 | 60; label: string }> = [
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
];

export async function getPharmacy(pharmacyId: string): Promise<BookingPharmacy | null> {
  logWidget("get_pharmacy_request", { pharmacyId });
  try {
    const { getDb } = await import("./firebase");
    const { doc, getDoc } = await import("firebase/firestore");
    const db = getDb();
    const snap = await getDoc(doc(db, "pharmacies", pharmacyId));
    if (!snap.exists()) {
      logWidgetError("get_pharmacy_not_found", { pharmacyId });
      return null;
    }
    const data = snap.data();
    logWidget("get_pharmacy_response", {
      pharmacyId,
      resolvedId: snap.id,
      chainId: data.chainId ?? null,
      ccsPharmacyId: data.ccsPharmacyId ?? null,
      supersededBy: data.supersededBy ?? null,
    });
    return { id: snap.id, ...data } as BookingPharmacy;
  } catch (err) {
    logWidgetError("get_pharmacy_error", {
      pharmacyId,
      message: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

// Rooms are auto-provisioned server-side as needed at booking time and are
// no longer surfaced to patients. This is kept only in case other read paths
// need a read-only room list; the booking flow itself never selects a room.
export async function getRooms(pharmacyId: string): Promise<BookingRoom[]> {
  const { getDb } = await import("./firebase");
  const { collection, getDocs, query, where } = await import("firebase/firestore");
  const db = getDb();
  const q = query(
    collection(db, "pharmacies", pharmacyId, "rooms"),
    where("isActive", "==", true),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as BookingRoom));
}

// `pharmacistId` is optional: when omitted, slots are the union of every
// pharmacist's personal availability at the pharmacy (the normal patient
// booking flow, which no longer asks patients to pick a room or pharmacist).
// `slotDurationMinutes` is the duration the patient chose in the booking
// flow's duration step (see BookingPage.tsx) — the server generates slots
// of exactly this length.
export async function getAvailableSlots(
  pharmacyId: string,
  startDate: string,
  endDate: string,
  slotDurationMinutes: number,
  pharmacistId?: string,
): Promise<TimeSlot[]> {
  const url = new URL(`${FUNCTIONS_BASE}/ccsGetAvailableSlots`);
  url.searchParams.set("pharmacyId", pharmacyId);
  if (pharmacistId) url.searchParams.set("pharmacistId", pharmacistId);
  url.searchParams.set("startDate", startDate);
  url.searchParams.set("endDate", endDate);
  url.searchParams.set("slotDurationMinutes", String(slotDurationMinutes));

  const requestCtx = { pharmacyId, pharmacistId: pharmacistId ?? null, startDate, endDate, slotDurationMinutes };
  logWidget("get_slots_request", requestCtx);

  let res: Response;
  try {
    const { appCheckHeader } = await import("./firebase");
    res = await fetch(url.toString(), { headers: await appCheckHeader() });
  } catch (err) {
    // A network-level failure (offline, DNS, CORS, blocked request) — the
    // caller's UI must not render this the same as "zero slots"; it needs
    // the real cause to escape the swallowed-catch pattern this task exists
    // to fix. See BookingPage.tsx's loadSlots for how the caller surfaces it.
    logWidgetError("get_slots_network_error", {
      ...requestCtx,
      message: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // response wasn't JSON — leave body null, status is still logged
    }
    logWidgetError("get_slots_http_error", {
      ...requestCtx,
      status: res.status,
      body,
    });
    throw new Error(`Failed to fetch slots (HTTP ${res.status})`);
  }

  const data = await res.json();
  const slots = data.slots as TimeSlot[];
  logWidget("get_slots_response", {
    ...requestCtx,
    slotCount: slots.length,
    availableCount: slots.filter((s) => s.available).length,
  });
  return slots;
}

export interface ManageBooking {
  bookingId: string;
  /** null for legacy bookings created before pharmacist assignment existed. */
  pharmacistId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  /** The booking's ORIGINAL appointment length, in minutes. */
  durationMinutes: number;
  status: string;
}

/**
 * Load the scheduling facts (assigned pharmacist + original duration) for a
 * booking the caller holds a management token for. The reschedule picker
 * needs these so it offers exactly the times the reschedule submission will
 * accept — the server only ever validates against the assigned pharmacist's
 * own schedule.
 */
export async function getBookingForManage(params: {
  pharmacyId: string;
  bookingId: string;
  token: string;
}): Promise<ManageBooking> {
  const url = new URL(`${FUNCTIONS_BASE}/ccsGetBookingForManage`);
  url.searchParams.set("pharmacyId", params.pharmacyId);
  url.searchParams.set("bookingId", params.bookingId);
  url.searchParams.set("token", params.token);

  logWidget("get_booking_request", { pharmacyId: params.pharmacyId, bookingId: params.bookingId });
  const { appCheckHeader } = await import("./firebase");

  let res: Response;
  try {
    res = await fetch(url.toString(), { headers: await appCheckHeader() });
  } catch (err) {
    logWidgetError("get_booking_network_error", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      message: err instanceof Error ? err.message : String(err),
    });
    throw new Error("Could not reach the booking service. Please check your connection and try again.");
  }

  if (!res.ok) {
    let body: { error?: string } | null = null;
    try {
      body = (await res.json()) as { error?: string };
    } catch {
      // non-JSON response — status is still logged below
    }
    logWidgetError("get_booking_http_error", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      status: res.status,
      body,
    });
    throw new Error(body?.error ?? `Could not load your booking (HTTP ${res.status})`);
  }

  const data = (await res.json()) as ManageBooking;
  logWidget("get_booking_response", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
    pharmacistId: data.pharmacistId,
    durationMinutes: data.durationMinutes,
    status: data.status,
  });
  return data;
}

export async function createBooking(params: {
  pharmacyId: string;
  pharmacistId?: string;
  condition?: string;
  date: string;
  startTime: string;
  endTime: string;
  patient: PatientPreFill;
}): Promise<{ bookingId: string; status: string; detailsToken?: string }> {
  // Note: startTime/endTime already encode the patient's chosen duration —
  // the server derives + validates the duration from them (see
  // validateSlotAgainstAvailability), so no separate field is sent here.
  const { appCheckHeader } = await import("./firebase");

  // Encrypt patient data client-side before sending to the server.
  // The server stores only ciphertext — it never sees plaintext patient fields.
  const publicKey = await getChainBookingPublicKey(params.pharmacyId);
  if (!publicKey) {
    throw new Error(
      "Booking encryption key is not yet available for this pharmacy. " +
        "Please try again in a moment.",
    );
  }
  const encryptedPatient = await hybridEncryptJson(publicKey, params.patient);

  const { patient, ...rest } = params;
  // patientEmail is sent separately so the server can send a confirmation email
  // without ever reading the encrypted patient payload.
  const body = { ...rest, encryptedPatient, patientEmail: patient.email };

  const res = await fetch(`${FUNCTIONS_BASE}/ccsCreateBooking`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await appCheckHeader()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? "Failed to create booking");
  }
  return res.json();
}

export async function cancelBooking(params: {
  pharmacyId: string;
  bookingId: string;
  token: string;
}): Promise<void> {
  const { appCheckHeader } = await import("./firebase");
  logWidget("cancel_booking_request", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
  });
  const res = await fetch(`${FUNCTIONS_BASE}/ccsCancelBooking`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await appCheckHeader()) },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let err: { error?: string } | null = null;
    try {
      err = (await res.json()) as { error?: string };
    } catch {
      // non-JSON response — status is still logged below
    }
    // Log the pharmacyId/bookingId alongside the status so a "Booking not
    // found" report can be traced back to the exact link the patient used.
    logWidgetError("cancel_booking_http_error", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      status: res.status,
      body: err,
    });
    throw new Error(err?.error ?? `Failed to cancel booking (HTTP ${res.status})`);
  }
  logWidget("cancel_booking_response", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
  });
}

export async function rescheduleBooking(params: {
  pharmacyId: string;
  bookingId: string;
  token: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
}): Promise<void> {
  const { appCheckHeader } = await import("./firebase");
  logWidget("reschedule_booking_request", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
    newDate: params.newDate,
    newStartTime: params.newStartTime,
    newEndTime: params.newEndTime,
  });
  const res = await fetch(`${FUNCTIONS_BASE}/ccsRescheduleBooking`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await appCheckHeader()) },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let err: { error?: string } | null = null;
    try {
      err = (await res.json()) as { error?: string };
    } catch {
      // non-JSON response — status is still logged below
    }
    logWidgetError("reschedule_booking_http_error", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      newDate: params.newDate,
      newStartTime: params.newStartTime,
      status: res.status,
      body: err,
    });
    throw new Error(err?.error ?? `Failed to reschedule booking (HTTP ${res.status})`);
  }
  logWidget("reschedule_booking_response", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
  });
}

/**
 * Optional "Speed up your consultation" details the patient can submit after
 * their booking is confirmed.
 *
 * ⚠️ DRIFT WARNING: the decrypted wire shape is documented as
 * `BookingExtraDetails` in artifacts/functions/src/types/booking.ts —
 * change both together.
 */
export interface BookingExtraDetails {
  addressLine1: string;
  addressLine2: string;
  eircode: string;
  ppsn: string;
  gpName: string;
  gpPractice: string;
  medications: string;
  allergies: string;
  medicalHistory: string;
}

/**
 * Encrypts the post-booking details client-side with the chain's booking
 * public key (same hybrid format as `encryptedPatient`) and stores the
 * ciphertext on the booking. The server never sees plaintext.
 */
export async function updateBookingDetails(params: {
  pharmacyId: string;
  bookingId: string;
  /**
   * Capability token from the createBooking response — the server's only
   * proof that this caller is the patient who made the booking.
   */
  token: string;
  details: BookingExtraDetails;
}): Promise<void> {
  const { appCheckHeader } = await import("./firebase");

  if (!params.token) {
    logWidgetError("update_booking_details_no_token", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
    });
    throw new Error("This booking session can no longer save extra details.");
  }

  const publicKey = await getChainBookingPublicKey(params.pharmacyId);
  if (!publicKey) {
    logWidgetError("update_booking_details_no_key", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
    });
    throw new Error(
      "Booking encryption key is not yet available for this pharmacy. " +
        "Please try again in a moment.",
    );
  }
  const encryptedDetails = await hybridEncryptJson(publicKey, params.details);

  logWidget("update_booking_details_request", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
  });
  const res = await fetch(`${FUNCTIONS_BASE}/ccsUpdateBookingDetails`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await appCheckHeader()) },
    body: JSON.stringify({
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      token: params.token,
      encryptedDetails,
    }),
  });
  if (!res.ok) {
    let err: { error?: string } | null = null;
    try {
      err = (await res.json()) as { error?: string };
    } catch {
      // non-JSON response — status is still logged below
    }
    logWidgetError("update_booking_details_http_error", {
      pharmacyId: params.pharmacyId,
      bookingId: params.bookingId,
      status: res.status,
      body: err,
    });
    throw new Error(err?.error ?? `Failed to save details (HTTP ${res.status})`);
  }
  logWidget("update_booking_details_response", {
    pharmacyId: params.pharmacyId,
    bookingId: params.bookingId,
  });
}
